import { useRef } from "react";
import { useTranslation } from "react-i18next";

const LEN = 6;

/** Pad to 6 slots; spaces mark empty boxes. */
function slots(value: string): string[] {
  return (value + " ".repeat(LEN)).slice(0, LEN).split("");
}

export interface OtpProps {
  /** Up to six digits. A gap left by deleting a middle digit is a space. */
  value: string;
  onChange: (next: string) => void;
  invalid?: boolean;
  disabled?: boolean;
  autoFocus?: boolean;
  /** Group label; defaults to "6-digit code". */
  label?: string;
  /** Called once all six boxes hold a digit (typed or pasted). */
  onComplete?: (code: string) => void;
}

/** True when an Otp value is a complete 6-digit code. */
export function otpComplete(value: string): boolean {
  return /^\d{6}$/.test(value);
}

/**
 * Six one-digit boxes (the design's `Otp`, ob-entry.jsx). Typing advances,
 * Backspace on an empty box steps back and clears the previous digit, and
 * pasting any text containing digits fills the boxes from the start.
 * Always laid out LTR, even in Arabic — codes are read left to right.
 */
export function Otp({ value, onChange, invalid, disabled, autoFocus, label, onComplete }: OtpProps) {
  const { t } = useTranslation();
  const refs = useRef<Array<HTMLInputElement | null>>([]);
  const v = slots(value);

  const emit = (arr: string[]) => {
    const next = arr.join("").replace(/\s+$/, "");
    onChange(next);
    if (onComplete && otpComplete(next)) onComplete(next);
  };
  const put = (i: number, ch: string) => {
    const arr = slots(value);
    arr[i] = ch || " ";
    emit(arr);
  };

  return (
    <div
      dir="ltr"
      style={{ display: "flex", gap: 8 }}
      role="group"
      aria-label={label ?? t("onboarding.kit.otp.label", "6-digit code")}
    >
      {v.map((d, i) => (
        <input
          key={i}
          ref={(el) => {
            refs.current[i] = el;
          }}
          className="ob-in ob-mono"
          inputMode="numeric"
          autoComplete={i === 0 ? "one-time-code" : "off"}
          maxLength={1}
          value={d.trim()}
          disabled={disabled}
          autoFocus={autoFocus && i === 0}
          aria-label={t("onboarding.kit.otp.digit", { defaultValue: "Digit {{n}}", n: i + 1 })}
          aria-invalid={invalid || undefined}
          style={{ width: 46, height: 52, padding: 0, textAlign: "center", fontSize: 20, fontWeight: 600 }}
          onFocus={(e) => e.currentTarget.select()}
          onPaste={(e) => {
            const digits = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, LEN);
            if (!digits) return;
            e.preventDefault();
            emit(digits.split(""));
            refs.current[Math.min(digits.length, LEN - 1)]?.focus();
          }}
          onKeyDown={(e) => {
            if (e.key === "Backspace" && !d.trim() && i > 0) {
              e.preventDefault();
              put(i - 1, "");
              refs.current[i - 1]?.focus();
            } else if (e.key === "ArrowLeft" && i > 0) {
              e.preventDefault();
              refs.current[i - 1]?.focus();
            } else if (e.key === "ArrowRight" && i < LEN - 1) {
              e.preventDefault();
              refs.current[i + 1]?.focus();
            }
          }}
          onChange={(e) => {
            const ch = e.target.value.replace(/\D/g, "").slice(-1);
            put(i, ch);
            if (ch && i < LEN - 1) refs.current[i + 1]?.focus();
          }}
        />
      ))}
    </div>
  );
}
